import React, {useCallback, useMemo, useEffect} from 'react'
import $ from 'jquery'
import {decode} from 'entities'
import removeMarkdownAndHtml from 'remove-markdown-and-html'

// Shared helpers for the react-select style components.
// It includes formatting options with images and tooltips and custom placeholders
// It will need some variables from useRailsSelect to work properly.
const useOptionFormatting = (
  options,
  {
    iconOnlySelection,
    showSubtitle = true,
    placeholderData,
    wrapperRef,
    menuIsOpen,
    changeInProgress,
  },
) => {
  const hasImageDataInOptions = useMemo(
    () => options.some((option) => option.imageData),
    [options],
  )

  const optionImage = useCallback((imageData) => {
    if (!imageData)
      return null

    return React.createElement(
      'div',
      {className: `rails_select--option-img ${imageData.css_classes}`},
      imageData.fa_icon
        ? React.createElement('i', {className: `fa ${imageData.fa_icon} icon-element`})
        : React.createElement('img', {src: imageData.image, alt: ''}),
    )
  }, [])

  const convertToPlainText = (str) => {
    if (!str)
      return ''

    // Decode HTML entities and remove markdown/HTML
    return decode(
      removeMarkdownAndHtml(str)
        .replace(/[\n\r]+/g, ' ')
        .replace(/~/g, ''),
    )
  }

  const optionTitleAndSubtitle = useCallback((option) => React.createElement(
    'span',
    {className: 'rails_select--option-title-container'},
    React.createElement('span', {className: 'rails_select--option-title'}, convertToPlainText(option.label)),
    option.subtitle && showSubtitle
    && React.createElement('span', {className: 'rails_select--option-subtitle'}, convertToPlainText(option.subtitle)),
  ), [showSubtitle])

  const formatOptionLabel = useCallback((option) => React.createElement(
    'span',
    {className: 'rails_select--option'},
    hasImageDataInOptions && optionImage(option.imageData),
    optionTitleAndSubtitle(option),
  ), [optionImage, hasImageDataInOptions, optionTitleAndSubtitle])

  const iconOnlySingleValue = useCallback(({data}) => React.createElement(
    'div',
    {className: 'rails_select__icon-only-single-value'},
    React.createElement(
      'span',
      {className: 'rails_select--option js-with-tooltip', title: convertToPlainText(data.label)},
      optionImage(data.imageData),
    ),
  ), [optionImage])

  const customPlaceholder = useCallback(({selectProps}) => {
    const {placeholder} = selectProps

    return React.createElement(
      'div',
      {
        className: `rails_select__custom-placeholder ${
          iconOnlySelection ? 'icon-only-single-value' : ''
        }`,
      },
      React.createElement(
        'div',
        {className: 'rails_select--option'},
        optionImage(placeholder.imageData),
        !iconOnlySelection
        && React.createElement(
          'span',
          {className: 'rails_select--option-title'},
          placeholder.label,
        ),
      ),
    )
  }, [optionImage, iconOnlySelection])

  const components = useMemo(() => {
    const result = {}
    if (iconOnlySelection)
      result.SingleValue = iconOnlySingleValue
    if (placeholderData)
      result.Placeholder = customPlaceholder
    return result
  }, [iconOnlySelection, placeholderData, iconOnlySingleValue, customPlaceholder])

  useEffect(() => {
    if (!iconOnlySelection)
      return undefined

    const tooltipSelector = '.rails_select--option.js-with-tooltip'
    const imagesWithUrls = document.querySelectorAll(`${tooltipSelector} img`)
    const selectWrapper = wrapperRef.current

    const handleImageLoad = (event) => {
      const imageElement = event.currentTarget
      if (imageElement instanceof HTMLImageElement) {
        const parentContainer = imageElement.closest(tooltipSelector)
        if (parentContainer)
          $(parentContainer).tooltip()
      }
    }

    if (imagesWithUrls.length > 0) {
      imagesWithUrls.forEach((img) => {
        const imageElement = img
        if (imageElement.complete)
          handleImageLoad({currentTarget: imageElement})
        else
          imageElement.addEventListener('load', handleImageLoad)
      })
    }

    const showTooltip = (event) => {
      if (!menuIsOpen && !changeInProgress)
        $(event.currentTarget).tooltip('show')
    }

    const hideTooltip = (event) => {
      $(event.currentTarget).tooltip('hide')
    }

    $(tooltipSelector).on('mouseenter focus', showTooltip)
    $(tooltipSelector).on('mouseleave blur', hideTooltip)

    if (selectWrapper) {
      $(selectWrapper).on('mousedown', () => {
        $(tooltipSelector).tooltip('hide')
      })
    }

    return () => {
      $(tooltipSelector).off('mouseenter focus', showTooltip)
      $(tooltipSelector).off('mouseleave blur', hideTooltip)

      if (selectWrapper)
        $(selectWrapper).off('mousedown')
    }
  }, [iconOnlySelection, options, menuIsOpen, changeInProgress, wrapperRef])

  return {hasImageDataInOptions, formatOptionLabel, components}
}

export default useOptionFormatting
